<template>
    <KHero
        v-editable="blok"
        class="hero component component--full-width"
        :eyebrow="blok.badge"
        :title="blok.title"
        :text="blok.subtitle"
    >
        <template #media>
            <NuxtImg
                v-if="blok.image?.filename"
                fit="cover"
                format="auto"
                densities="1x"
                :width="image.width"
                :height="image.height"
                :sizes="image.sizes"
                :src="blok.image.filename"
                :alt="blok.image.alt"
                :loading="priority ? 'eager' : 'lazy'"
                :fetchpriority="priority ? 'high' : undefined"
                :preload="priority ? { fetchPriority: 'high' } : false"
            />
        </template>

        <template
            v-if="blok.stats?.length"
            #footer
        >
            <div
                class="hero__stats"
            >
                <hero-stat
                    v-for="stat in blok.stats"
                    :key="stat._uid"
                    :stat="stat"
                />
            </div>
        </template>
    </KHero>
</template>

<script setup>
import { KHero } from '@vemedia/karibu'
import heroImageSizes from '~/helpers/heroImageSizes'

const props = defineProps({
    blok: {
        type: Object,
        default: () => ({}),
    },

    // The parent decides whether THIS hero is the page's LCP image and forwards
    // `priority` through StoryblokComponent's attr fallthrough. It's opt-in, not
    // derived from blok position: an article/info-page can open its content with
    // a hero, but the ArticleBanner above it stays the sole priority image, so
    // no render position may flip a hero to priority on those pages. Eager
    // loading, fetchpriority and the `preload` prop are gated together —
    // shipping only some of them is how two high-priority images end up
    // fighting for bandwidth. The preload <link> is emitted by NuxtImg itself
    // (imagesrcset/imagesizes), byte-matching the rendered srcset.
    priority: {
        type: Boolean,
        default: false,
    },
})

// The hero is full-bleed and CSS-cropped to this ratio at every breakpoint, so
// it is the ratio to request — not the upload's own.
const RATIO = 1920 / 900

const img = useImage()

// Falls back to the design size for assets with no size segment in the URL
// (external images), which is the old unconditional behaviour.
const image = computed(() => heroImageSizes(props.blok.image?.filename, RATIO, img.options.screens) ?? {
    width: 1920,
    height: 900,
    sizes: 'xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw',
})
</script>

<style lang="scss">
@use 'hero';
</style>
